import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import {
  defaultFilters,
  RecordsListControls,
  type RecordsListFilters,
} from '@liza/ui/components/records/RecordsListControls';
import { SurveysList } from '@liza/ui/components/records/SurveysList';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedSurveyIdentifier?: string;
};

// Read-only: survey scores are computed server-side from QuestionnaireResponses, never entered by hand.
export const Surveys = ({ onViewChange, patient, selectedSurveyIdentifier }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  const {
    items: surveys,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.observation.list.queryKey({
      medplumPatientId: patient.medplumId,
      category: 'surveys',
      options: listOptions,
    }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.observation.list.query({
        medplumPatientId: patient.medplumId,
        category: 'surveys',
        options: listOptions,
        offset,
        count,
      });
      return { items: res.observations, hasMore: res.hasMore };
    },
  });

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('surveys')}
        searchPlaceholder={t('searchSurveys')}
        onFiltersChange={handleFiltersChange}
      />
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <SurveysList
          surveys={surveys}
          onSelectSurvey={(surveyIdentifier) => onViewChange({ type: 'survey-detail', surveyIdentifier })}
          selectedSurveyIdentifier={selectedSurveyIdentifier}
        />
      </RecordListScroll>
    </div>
  );
};
